import React from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PageContainer from "../layout/PageContainer";
import useNewsletterSubscriptions from "../../hooks/useNewsletterSubscriptions";

function formatBerlinDate(value) {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return new Intl.DateTimeFormat("de-DE", {
        dateStyle: "medium",
        timeStyle: "short",
        timeZone: "Europe/Berlin",
    }).format(date);
}

export default function NewsletterSubscriptionsPage() {
    const { subscriptions, loading, error, fetchSubscriptions, setSubscriptionActive } = useNewsletterSubscriptions();
    const [search, setSearch] = React.useState("");
    const [status, setStatus] = React.useState("all");
    const [busyId, setBusyId] = React.useState(null);
    const [actionError, setActionError] = React.useState("");
    const [details, setDetails] = React.useState(null);

    const filters = React.useMemo(() => ({ q: search.trim(), status }), [search, status]);

    React.useEffect(() => {
        const id = window.setTimeout(() => {
            fetchSubscriptions(filters);
        }, 250);
        return () => window.clearTimeout(id);
    }, [fetchSubscriptions, filters]);

    const toggleActive = async (row, isActive) => {
        setBusyId(row.id);
        setActionError("");
        try {
            await setSubscriptionActive(row.id, isActive, filters);
        } catch (e) {
            setActionError(e?.response?.data?.error || e.message || "Status konnte nicht geändert werden.");
        } finally {
            setBusyId(null);
        }
    };

    return (
        <PageContainer
            title="Newsletter-Anmeldungen"
            actions={
                <Button variant="outlined" onClick={() => fetchSubscriptions(filters)} disabled={loading}>
                    Aktualisieren
                </Button>
            }
        >
            {(error || actionError) && <Alert severity="error">{error || actionError}</Alert>}

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 220px" }, gap: 2, mb: 2 }}>
                <TextField
                    fullWidth
                    size="small"
                    label="Newsletter-Anmeldung suchen"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
                <FormControl size="small" fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
                        <MenuItem value="all">Alle</MenuItem>
                        <MenuItem value="active">Aktiv</MenuItem>
                        <MenuItem value="blocked">Gesperrt</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            <TableContainer component={Paper} sx={{ maxHeight: "70vh", overflow: "auto" }}>
                {loading ? (
                    <Box sx={{ py: 6, textAlign: "center" }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>E-Mail</TableCell>
                                <TableCell>Sprache</TableCell>
                                <TableCell>Eingetragen am</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>IP-Adresse</TableCell>
                                <TableCell>Gesperrt am</TableCell>
                                <TableCell align="right">Aktionen</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {subscriptions.map((row) => {
                                const active = !!row.is_active;
                                return (
                                    <TableRow key={row.id} hover>
                                        <TableCell>{row.email}</TableCell>
                                        <TableCell>{String(row.language || "de").toUpperCase()}</TableCell>
                                        <TableCell>{formatBerlinDate(row.consent_given_at || row.created_at)}</TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                color={active ? "success" : "default"}
                                                label={active ? "Aktiv" : "Gesperrt"}
                                            />
                                        </TableCell>
                                        <TableCell>{row.ip_address || "-"}</TableCell>
                                        <TableCell>{formatBerlinDate(row.unsubscribed_at)}</TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Details anzeigen">
                                                <IconButton onClick={() => setDetails(row)}>
                                                    <InfoOutlinedIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            {active ? (
                                                <Tooltip title="Sperren">
                                                    <span>
                                                        <IconButton color="warning" disabled={busyId === row.id} onClick={() => toggleActive(row, false)}>
                                                            <BlockIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            ) : (
                                                <Tooltip title="Aktivieren">
                                                    <span>
                                                        <IconButton color="success" disabled={busyId === row.id} onClick={() => toggleActive(row, true)}>
                                                            <CheckCircleIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {!subscriptions.length && (
                                <TableRow>
                                    <TableCell colSpan={7}>
                                        <Typography color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
                                            Keine Newsletter-Anmeldungen gefunden.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                )}
            </TableContainer>

            <Dialog open={!!details} onClose={() => setDetails(null)} fullWidth maxWidth="md">
                <DialogTitle>Newsletter-Anmeldung</DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display: "grid", gap: 1.4 }}>
                        <Typography><b>E-Mail:</b> {details?.email}</Typography>
                        <Typography><b>Sprache:</b> {String(details?.language || "de").toUpperCase()}</Typography>
                        <Typography><b>Eingetragen am:</b> {formatBerlinDate(details?.consent_given_at || details?.created_at)}</Typography>
                        <Typography><b>Status:</b> {details?.is_active ? "Aktiv" : "Gesperrt"}</Typography>
                        <Typography><b>IP-Adresse:</b> {details?.ip_address || "-"}</Typography>
                        <Typography><b>User-Agent:</b> {details?.user_agent || "-"}</Typography>
                        <Box>
                            <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Einwilligungstext</Typography>
                            <Paper variant="outlined" sx={{ p: 2, bgcolor: "#f8fafc" }}>
                                <Typography sx={{ whiteSpace: "pre-wrap" }}>{details?.consent_text || "-"}</Typography>
                            </Paper>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetails(null)}>Schließen</Button>
                </DialogActions>
            </Dialog>
        </PageContainer>
    );
}
